import { useState, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import API from "../services/api";
import MainLayout from "../layouts/MainLayout";

export default function SalesClaim() {
  const [form, setForm] = useState({});
  const [qrValue, setQrValue] = useState("");
  const [scanOpen, setScanOpen] = useState(false);
  const [verified, setVerified] = useState(false);
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const scannerRef = useRef(null);

  // ==========================
  // 📷 START CAMERA SCAN (FIXED)
  // ==========================
  const startScanner = () => {
    setScanOpen(true);

    setTimeout(() => {
      scannerRef.current = new Html5Qrcode("reader");

      scannerRef.current.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: 250 },
        async (decodedText) => {
          try {
            await API.post("/qr/scan", {
              qr_value: decodedText
            });

            // ✅ SUCCESS
            setQrValue(decodedText);
            setForm((prev) => ({
              ...prev,
              qr_value: decodedText
            }));

            setVerified(true);

            // 🔥 STOP CAMERA SAFELY
            await scannerRef.current.stop();
            scannerRef.current.clear();

            setScanOpen(false);

            // 📳 Mobile vibration feedback
            if (navigator.vibrate) navigator.vibrate(200);

          } catch (err) {
            alert(err.response?.data?.msg || "Invalid QR");
          }
        }
      );
    }, 300); // ✅ FIX FOR "reader not found"
  };

  // ==========================
  // ❌ CLOSE SCANNER
  // ==========================
  const closeScanner = async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      }
    } catch {}

    setScanOpen(false);
  };

  // ==========================
  // 📤 SUBMIT CLAIM
  // ==========================
  const submitClaim = async () => {
    try {
      setLoading(true);

      if (!qrValue) return alert("Scan QR first");
      if (!form.customer_mobile) return alert("Enter mobile");

      const fd = new FormData();

      Object.keys(form).forEach((k) => {
        fd.append(k, form[k]);
      });

      await API.post("/claim", fd);

      alert("✅ OTP sent on WhatsApp");
      setStep(2);

    } catch (err) {
      alert(err.response?.data?.msg || "Error submitting claim");
    } finally {
      setLoading(false);
    }
  };

  // ==========================
  // 🔐 VERIFY OTP
  // ==========================
  const verifyOTP = async () => {
    try {
      setLoading(true);

      await API.post("/verify-otp", {
        mobile: form.customer_mobile,
        otp,
        qr_value: qrValue
      });

      alert("🎉 Claim submitted successfully!");

      // RESET
      setForm({});
      setQrValue("");
      setVerified(false);
      setOtp("");
      setStep(1);

    } catch (err) {
      alert("❌ Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div style={{
        maxWidth: 400,
        margin: "auto",
        padding: 15
      }}>
        <h2>Sales Claim</h2>

        {/* ================= STEP 1 ================= */}
        {step === 1 && (
          <>
            <h3>📷 Scan QR</h3>

            {!verified ? (
              <button onClick={startScanner} style={{
                width: "100%",
                padding: 12,
                fontSize: 16,
                borderRadius: 8,
                background: "#007bff",
                color: "#fff",
                border: "none"
              }}>
                📷 Tap to Scan QR
              </button>
            ) : (
              <div style={{
                background: "#d4edda",
                padding: 12,
                borderRadius: 8,
                color: "green",
                textAlign: "center"
              }}>
                ✅ QR Verified
                <br />
                <small>{qrValue}</small>
              </div>
            )}

            {/* 📷 CAMERA FULLSCREEN STYLE */}
            {scanOpen && (
              <div style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: "#000",
                zIndex: 9999
              }}>
                <div id="reader" style={{
                  width: "100%",
                  height: "100%"
                }}></div>

                <button onClick={closeScanner} style={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  padding: "10px 15px",
                  background: "red",
                  color: "#fff",
                  border: "none",
                  borderRadius: 5
                }}>
                  ✕ Close
                </button>
              </div>
            )}

            <hr />

            {/* ================= FORM ================= */}
            {verified && (
              <>
                <h3>👤 Customer Details</h3>

                <input placeholder="Customer Name"
                  onChange={(e) =>
                    setForm({ ...form, customer_name: e.target.value })
                  } />

                <input placeholder="Mobile"
                  onChange={(e) =>
                    setForm({ ...form, customer_mobile: e.target.value })
                  } />

                <input placeholder="CNIC"
                  onChange={(e) =>
                    setForm({ ...form, customer_cnic: e.target.value })
                  } />

                <input placeholder="Address"
                  onChange={(e) =>
                    setForm({ ...form, address: e.target.value })
                  } />

                <input placeholder="City"
                  onChange={(e) =>
                    setForm({ ...form, city: e.target.value })
                  } />

                <input type="file"
                  onChange={(e) =>
                    setForm({ ...form, invoice: e.target.files[0] })
                  } />

                <button onClick={submitClaim} disabled={loading} style={{
                  width: "100%",
                  marginTop: 10,
                  padding: 12,
                  background: "green",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8
                }}>
                  {loading ? "Submitting..." : "Submit Claim"}
                </button>
              </>
            )}
          </>
        )}

        {/* ================= STEP 2 ================= */}
        {step === 2 && (
          <>
            <h3>🔐 OTP Verification</h3>

            <input
              placeholder="Enter OTP"
              onChange={(e) => setOtp(e.target.value)}
            />

            <button onClick={verifyOTP} disabled={loading} style={{
              width: "100%",
              marginTop: 10,
              padding: 12,
              background: "#007bff",
              color: "#fff",
              border: "none",
              borderRadius: 8
            }}>
              {loading ? "Verifying..." : "Verify OTP"}
            </button>
          </>
        )}
      </div>
    </MainLayout>
  );
}